import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { FormField, ValidationError } from '../schema/types';
import { FormValidator } from '../utils/validation';

@customElement('jupiter-form-field')
export class JupiterFormField extends LitElement {
  @property({ type: Object }) field!: FormField;
  @property({ type: String }) conceptId!: string;
  @property({ type: String }) columnId!: string;
  @property() value: any = null;
  @property({ type: Boolean }) disabled = false;
  @property({ type: String }) locale = 'en-US';
  @property({ type: Boolean }) hideLabel = false;

  @state() private _errors: ValidationError[] = [];
  @state() private _touched = false;

  static styles = css`
    :host {
      display: block;
      margin-bottom: 0px; /* Remove bottom margin for table layout */
    }

    :host([hideLabel]) {
      margin-bottom: 0px;
    }

    .field-container {
      position: relative;
    }

    .field-label {
      display: block;
      font-weight: 500;
      margin-bottom: 4px;
      color: var(--jupiter-text-primary, #333);
      font-size: 14px;
    }

    .field-label.required::after {
      content: ' *';
      color: var(--jupiter-error-color, #d32f2f);
    }

    .field-input {
      width: 100%;
      padding: 6px 8px; /* Reduced padding for table cells */
      border: 1px solid var(--jupiter-border-color, #ddd);
      border-radius: 4px;
      font-size: 13px; /* Slightly smaller font for table */
      font-family: inherit;
      background: var(--jupiter-input-background, #fff);
      color: var(--jupiter-text-primary, #333);
      box-sizing: border-box;
    }

    .field-input:focus {
      outline: none;
      border-color: var(--jupiter-primary-color, #1976d2);
      box-shadow: 0 0 0 2px var(--jupiter-primary-color-light, rgba(25, 118, 210, 0.2));
    }

    .field-input:disabled {
      background: var(--jupiter-disabled-background, #f5f5f5);
      color: var(--jupiter-disabled-text, #999);
      cursor: not-allowed;
    }

    .field-input.error {
      border-color: var(--jupiter-error-color, #d32f2f);
    }

    .field-input.warning {
      border-color: var(--jupiter-warning-color, #ff9800);
    }

    .field-errors {
      margin-top: 4px;
    }

    .field-error {
      display: block;
      font-size: 12px;
      margin-bottom: 2px;
    }

    .field-error.error {
      color: var(--jupiter-error-color, #d32f2f);
    }

    .field-error.warning {
      color: var(--jupiter-warning-color, #ff9800);
    }

    .field-error.info {
      color: var(--jupiter-info-color, #2196f3);
    }

    select.field-input {
      appearance: none;
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
      background-position: right 8px center;
      background-repeat: no-repeat;
      background-size: 16px;
      padding-right: 32px;
    }

    textarea.field-input {
      resize: vertical;
      min-height: 60px;
    }

    .checkbox-container {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .checkbox-container input[type="checkbox"] {
      margin: 0;
      width: auto;
    }
  `;

  protected willUpdate(changedProperties: PropertyValues) {
    if (changedProperties.has('value') || changedProperties.has('field')) {
      this._validateField();
    }
  }

  private _validateField() {
    if (this.field?.validation) {
      this._errors = FormValidator.validateField(
        this.field.id,
        this.conceptId,
        this.columnId,
        this.value,
        this.field.type,
        this.field.validation
      );
    }
  }

  private _handleInput(event: Event) {
    const target = event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    let value: any = target.value;

    // Convert value based on field type
    if (this.field.type === 'number' || this.field.type === 'decimal') {
      value = value === '' ? null : Number(value);
    } else if (this.field.type === 'boolean') {
      value = (target as HTMLInputElement).checked;
    }

    this.value = value;
    this._touched = true;
    
    this.dispatchEvent(new CustomEvent('field-change', {
      detail: {
        fieldId: this.field.id,
        conceptId: this.conceptId,
        columnId: this.columnId,
        value,
        oldValue: this.value
      },
      bubbles: true
    }));
  }

  private _handleFocus() {
    this.dispatchEvent(new CustomEvent('field-focus', {
      detail: {
        fieldId: this.field.id,
        conceptId: this.conceptId,
        columnId: this.columnId
      },
      bubbles: true
    }));
  }

  private _handleBlur() {
    this._touched = true;
    this.dispatchEvent(new CustomEvent('field-blur', {
      detail: {
        fieldId: this.field.id,
        conceptId: this.conceptId,
        columnId: this.columnId
      },
      bubbles: true
    }));
  }

  private _renderInput() {
    const hasErrors = this._errors.some(e => e.severity === 'error');
    const hasWarnings = this._errors.some(e => e.severity === 'warning');
    const cssClass = `field-input ${hasErrors ? 'error' : hasWarnings ? 'warning' : ''}`;
    
    // Generate unique field identifiers for form submission
    const fieldId = `${this.conceptId}__${this.columnId}`;
    const fieldName = `data[${this.conceptId}][${this.columnId}]`;

    switch (this.field.type) {
      case 'textarea':
        return html`
          <textarea
            id="${fieldId}"
            name="${fieldName}"
            class="${cssClass}"
            .value="${this.value || ''}"
            ?disabled="${this.disabled || this.field.disabled}"
            placeholder="${this.field.placeholder || ''}"
            @input="${this._handleInput}"
            @focus="${this._handleFocus}"
            @blur="${this._handleBlur}"
          ></textarea>
        `;

      case 'select':
        return html`
          <select
            id="${fieldId}"
            name="${fieldName}"
            class="${cssClass}"
            .value="${this.value || ''}"
            ?disabled="${this.disabled || this.field.disabled}"
            @change="${this._handleInput}"
            @focus="${this._handleFocus}"
            @blur="${this._handleBlur}"
          >
            <option value="">Select...</option>
            ${this.field.options?.map(option => html`
              <option 
                value="${option.value}"
                ?disabled="${option.disabled}"
                ?selected="${this.value === option.value}"
              >
                ${option.label}
              </option>
            `)}
          </select>
        `;

      case 'boolean':
        return html`
          <div class="checkbox-container">
            <input
              id="${fieldId}"
              name="${fieldName}"
              type="checkbox"
              class="field-input"
              .checked="${Boolean(this.value)}"
              ?disabled="${this.disabled || this.field.disabled}"
              @change="${this._handleInput}"
              @focus="${this._handleFocus}"
              @blur="${this._handleBlur}"
            />
            <span>${this.field.label}</span>
          </div>
        `;

      default:
        return html`
          <input
            id="${fieldId}"
            name="${fieldName}"
            type="${this._getInputType()}"
            class="${cssClass}"
            .value="${this.value || ''}"
            ?disabled="${this.disabled || this.field.disabled}"
            placeholder="${this.field.placeholder || ''}"
            @input="${this._handleInput}"
            @focus="${this._handleFocus}"
            @blur="${this._handleBlur}"
          />
        `;
    }
  }

  private _getInputType(): string {
    switch (this.field.type) {
      case 'number':
      case 'decimal':
      case 'currency':
      case 'percentage':
        return 'number';
      case 'date':
        return 'date';
      case 'datetime':
        return 'datetime-local';
      case 'email':
        return 'email';
      case 'url':
        return 'url';
      case 'tel':
        return 'tel';
      default:
        return 'text';
    }
  }

  render() {
    const showLabel = !this.hideLabel && this.field.type !== 'boolean';

    return html`
      <div class="field-container">
        ${showLabel ? html`
          <label class="field-label ${this.field.required ? 'required' : ''}">
            ${this.field.label}
          </label>
        ` : ''}
        
        ${this._renderInput()}
        
        ${this._errors.length > 0 && this._touched ? html`
          <div class="field-errors">
            ${this._errors.map(error => html`
              <span class="field-error ${error.severity}">
                ${error.message}
              </span>
            `)}
          </div>
        ` : ''}
      </div>
    `;
  }
}
